import { useRef, useState, type FormEvent } from "react";
import {
  DEFAULT_AGENT_KEY,
  DEFAULT_TOKEN_ENDPOINT,
  createSession,
  listEvents,
  saveAgentCredentials,
  sessionOutcome,
  streamInput,
  verifyAgent,
  type AdminCredentials
} from "./api";
import { MemoryPanel } from "./MemoryPanel";
import { ReportView } from "./ReportView";
import { progressEntry, type ProgressEntry } from "./progress";
import {
  feedbackMessage,
  isSupportedContract,
  parseCustomerIds,
  requestMessage,
  sessionName,
  type ByteUnitSystem,
  type ReportInputs,
  type ReportResult
} from "./report";
import { readStored, writeStored } from "./storage";
import "./billingCalculator.css";

const API_KEY_STORAGE = "overage-reporter.api-key";
const AGENT_KEY_STORAGE = "overage-reporter.agent-key";

type CredentialSource = "agent" | "session";

type RunState =
  | { kind: "idle" }
  | { kind: "running"; sessionKey: string | null; progress: ProgressEntry[]; previous: ReportResult | null }
  | { kind: "done"; result: ReportResult; progress: ProgressEntry[] }
  | {
      kind: "failed";
      message: string;
      sessionKey: string | null;
      progress: ProgressEntry[];
      previous: ReportResult | null;
    };

interface Turn {
  inputs: ReportInputs;
  message: string;
  files: File[];
  sessionKey: string | null;
  previous: ReportResult | null;
  reviewerFeedback: string | null;
  firstProgress: ProgressEntry[];
}

function previousMonth(): { from: string; to: string } {
  const now = new Date();
  const first = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth() - 1, 1));
  const last = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), 0));
  return { from: first.toISOString().slice(0, 10), to: last.toISOString().slice(0, 10) };
}

function ProgressLog({ entries, running }: { entries: ProgressEntry[]; running: boolean }) {
  return (
    <ol className="progress no-print">
      {entries.map((entry, index) => (
        <li key={index} className={`progress-item ${entry.tone}`}>
          {entry.label}
        </li>
      ))}
      {running && <li className="progress-item pending">Working…</li>}
    </ol>
  );
}

export const BillingCalculator = () => {
  const defaults = previousMonth();
  const [apiKey, setApiKey] = useState(() => readStored("session", API_KEY_STORAGE) ?? "");
  const [rememberApiKey, setRememberApiKey] = useState(() => readStored("session", API_KEY_STORAGE) !== null);
  const [agentKey, setAgentKey] = useState(() => readStored("local", AGENT_KEY_STORAGE) ?? DEFAULT_AGENT_KEY);
  const [agentStatus, setAgentStatus] = useState<{ ok: boolean; text: string } | null>(null);

  const [credentialSource, setCredentialSource] = useState<CredentialSource>("agent");
  const [credentials, setCredentials] = useState<AdminCredentials>({
    clientId: "",
    clientSecret: "",
    tokenEndpoint: DEFAULT_TOKEN_ENDPOINT
  });
  const [credentialStatus, setCredentialStatus] = useState<{ ok: boolean; text: string } | null>(null);

  const [contract, setContract] = useState<File | null>(null);
  const [customerIdsText, setCustomerIdsText] = useState("");
  const [fromDate, setFromDate] = useState(defaults.from);
  const [toDate, setToDate] = useState(defaults.to);
  const [byteUnitSystem, setByteUnitSystem] = useState<ByteUnitSystem>("binary");
  const [formError, setFormError] = useState<string | null>(null);

  const [run, setRun] = useState<RunState>({ kind: "idle" });
  const [feedback, setFeedback] = useState("");
  const [completedRuns, setCompletedRuns] = useState(0);
  const abortRef = useRef<AbortController | null>(null);

  const credentialsComplete =
    credentials.clientId.trim() !== "" &&
    credentials.clientSecret.trim() !== "" &&
    credentials.tokenEndpoint.trim() !== "";

  function updateApiKey(value: string, remember: boolean) {
    setApiKey(value);
    setRememberApiKey(remember);
    writeStored("session", API_KEY_STORAGE, remember ? value : null);
  }

  function updateAgentKey(value: string) {
    setAgentKey(value);
    writeStored("local", AGENT_KEY_STORAGE, value);
    setAgentStatus(null);
  }

  async function checkAgent() {
    setAgentStatus(null);
    try {
      setAgentStatus({ ok: true, text: `Connected to “${await verifyAgent(apiKey.trim(), agentKey.trim())}”` });
    } catch (error) {
      setAgentStatus({ ok: false, text: (error as Error).message });
    }
  }

  async function storeCredentialsOnAgent() {
    setCredentialStatus(null);
    try {
      await saveAgentCredentials(apiKey.trim(), agentKey.trim(), trimmedCredentials());
      setCredentialStatus({ ok: true, text: "Saved as the agent's default Admin API credentials." });
    } catch (error) {
      setCredentialStatus({ ok: false, text: (error as Error).message });
    }
  }

  function trimmedCredentials(): AdminCredentials {
    return {
      clientId: credentials.clientId.trim(),
      clientSecret: credentials.clientSecret.trim(),
      tokenEndpoint: credentials.tokenEndpoint.trim()
    };
  }

  function validInputs(): ReportInputs | null {
    if (apiKey.trim() === "") return fail("Enter a Vectara API key.");
    if (agentKey.trim() === "") return fail("Enter the agent key.");
    if (!contract) return fail("Choose a contract file.");
    if (!isSupportedContract(contract.name)) return fail("The contract must be a .docx, .pdf or .txt file.");
    if (credentialSource === "session" && !credentialsComplete)
      return fail("Fill in the client ID, client secret and token endpoint, or use the agent's stored credentials.");
    let customerIds: string[];
    try {
      customerIds = parseCustomerIds(customerIdsText);
    } catch (error) {
      return fail((error as Error).message);
    }
    if (customerIds.length === 0) return fail("Enter at least one customer ID.");
    if (!fromDate || !toDate) return fail("Choose a from date and a to date.");
    if (toDate < fromDate) return fail("The to date must not be before the from date.");
    setFormError(null);
    return { contractFileName: contract.name, customerIds, fromDate, toDate, byteUnitSystem };
  }

  function fail(message: string): null {
    setFormError(message);
    return null;
  }

  async function runTurn(turn: Turn) {
    const controller = new AbortController();
    abortRef.current = controller;
    const progress: ProgressEntry[] = [...turn.firstProgress];
    let sessionKey = turn.sessionKey;
    setRun({ kind: "running", sessionKey, progress: [...progress], previous: turn.previous });
    try {
      if (sessionKey === null) {
        sessionKey = await createSession(
          apiKey.trim(),
          agentKey.trim(),
          sessionName(turn.inputs, new Date()),
          credentialSource === "session" ? trimmedCredentials() : null
        );
      }
      const activeSession = sessionKey;
      setRun({ kind: "running", sessionKey: activeSession, progress: [...progress], previous: turn.previous });
      await streamInput(
        apiKey.trim(),
        agentKey.trim(),
        activeSession,
        turn.message,
        turn.files,
        (agentEvent) => {
          const entry = progressEntry(agentEvent);
          if (entry) {
            progress.push(entry);
            setRun({ kind: "running", sessionKey: activeSession, progress: [...progress], previous: turn.previous });
          }
        },
        controller.signal
      );
      const outcome = sessionOutcome(await listEvents(apiKey.trim(), agentKey.trim(), activeSession));
      setCompletedRuns((count) => count + 1);
      if (!outcome.agentOutput) {
        const reason =
          outcome.errors.length > 0 ? outcome.errors.join("; ") : "The agent finished without producing a report.";
        setRun({ kind: "failed", message: reason, sessionKey, progress, previous: turn.previous });
        return;
      }
      setRun({
        kind: "done",
        progress,
        result: {
          generatedAt: new Date().toISOString(),
          sessionKey: activeSession,
          revision: (turn.previous?.revision ?? 0) + 1,
          reviewerFeedback: [
            ...(turn.previous?.reviewerFeedback ?? []),
            ...(turn.reviewerFeedback ? [turn.reviewerFeedback] : [])
          ],
          inputs: turn.inputs,
          agentOutput: outcome.agentOutput,
          report: outcome.report,
          verificationIssues: outcome.verificationIssues
        }
      });
    } catch (error) {
      const message = controller.signal.aborted ? "Cancelled." : (error as Error).message;
      setRun({ kind: "failed", message, sessionKey, progress, previous: turn.previous });
    } finally {
      abortRef.current = null;
    }
  }

  async function generate(event: FormEvent) {
    event.preventDefault();
    const inputs = validInputs();
    if (!inputs || !contract) return;
    await runTurn({
      inputs,
      message: requestMessage(inputs),
      files: [contract],
      sessionKey: null,
      previous: null,
      reviewerFeedback: null,
      firstProgress: []
    });
  }

  async function sendFeedback(event: FormEvent) {
    event.preventDefault();
    if (run.kind !== "done" || feedback.trim() === "") return;
    const previous = run.result;
    const text = feedback.trim();
    setFeedback("");
    await runTurn({
      inputs: previous.inputs,
      message: feedbackMessage(text),
      files: [],
      sessionKey: previous.sessionKey,
      previous,
      reviewerFeedback: text,
      firstProgress: [{ label: `Reviewer feedback: “${text}”`, tone: "info" }]
    });
  }

  const running = run.kind === "running";

  return (
    <div className="bc-root">
      <main className="layout">
        <aside className="sidebar no-print">
          <section className="panel">
            <h2>Connection</h2>
            <label className="field">
              <span>Vectara API key</span>
              <input
                type="password"
                name="vectara-api-key"
                autoComplete="new-password"
                data-1p-ignore
                data-lpignore="true"
                value={apiKey}
                onChange={(e) => updateApiKey(e.target.value, rememberApiKey)}
                placeholder="zut_…"
              />
            </label>
            <label className="check">
              <input
                type="checkbox"
                checked={rememberApiKey}
                onChange={(e) => updateApiKey(apiKey, e.target.checked)}
              />
              Keep for this browser session
            </label>
            <label className="field">
              <span>Agent key</span>
              <input value={agentKey} onChange={(e) => updateAgentKey(e.target.value)} />
            </label>
            <button
              type="button"
              className="button small"
              onClick={checkAgent}
              disabled={apiKey.trim() === "" || agentKey.trim() === ""}
            >
              Test connection
            </button>
            {agentStatus && <p className={agentStatus.ok ? "status ok" : "status error"}>{agentStatus.text}</p>}
          </section>

          <MemoryPanel apiKey={apiKey} agentKey={agentKey} completedRuns={completedRuns} />
          <section className="panel">
            <h2>Admin API credentials</h2>
            <div className="segmented" role="radiogroup">
              <button
                type="button"
                role="radio"
                aria-checked={credentialSource === "agent"}
                className={credentialSource === "agent" ? "active" : ""}
                onClick={() => setCredentialSource("agent")}
              >
                Stored on agent
              </button>
              <button
                type="button"
                role="radio"
                aria-checked={credentialSource === "session"}
                className={credentialSource === "session" ? "active" : ""}
                onClick={() => setCredentialSource("session")}
              >
                Use for this run
              </button>
            </div>
            <p className="hint">
              {credentialSource === "agent"
                ? "Runs use the OAuth client stored as agent secrets. Enter new values below and save to replace them."
                : "These values are sent as secrets of the new session only; the agent's stored credentials stay unchanged."}
            </p>
            <label className="field">
              <span>OAuth client ID</span>
              <input
                name="admin-oauth-client-id"
                autoComplete="off"
                data-1p-ignore
                data-lpignore="true"
                value={credentials.clientId}
                onChange={(e) => setCredentials({ ...credentials, clientId: e.target.value })}
              />
            </label>
            <label className="field">
              <span>OAuth client secret</span>
              <input
                type="password"
                name="admin-oauth-client-secret"
                autoComplete="new-password"
                data-1p-ignore
                data-lpignore="true"
                value={credentials.clientSecret}
                onChange={(e) => setCredentials({ ...credentials, clientSecret: e.target.value })}
              />
            </label>
            <label className="field">
              <span>OAuth token endpoint</span>
              <input
                value={credentials.tokenEndpoint}
                onChange={(e) => setCredentials({ ...credentials, tokenEndpoint: e.target.value })}
              />
            </label>
            {credentialSource === "agent" && (
              <button
                type="button"
                className="button small"
                onClick={storeCredentialsOnAgent}
                disabled={!credentialsComplete || apiKey.trim() === ""}
              >
                Save to agent
              </button>
            )}
            {credentialStatus && (
              <p className={credentialStatus.ok ? "status ok" : "status error"}>{credentialStatus.text}</p>
            )}
          </section>
        </aside>

        <div className="content">
          <form className="panel inputs no-print" onSubmit={generate}>
            <h2>Report inputs</h2>
            <label className={`dropzone ${contract ? "has-file" : ""}`}>
              <input type="file" accept=".docx,.pdf,.txt" onChange={(e) => setContract(e.target.files?.[0] ?? null)} />
              <span className="dropzone-title">{contract ? contract.name : "Choose the contract"}</span>
              <span className="muted">{contract ? `${(contract.size / 1024).toFixed(1)} KB` : "DOCX, PDF or TXT"}</span>
            </label>
            <label className="field">
              <span>Customer IDs</span>
              <textarea
                rows={3}
                value={customerIdsText}
                onChange={(e) => setCustomerIdsText(e.target.value)}
                placeholder="1311583708, 2045178213 — separated by commas, spaces or new lines"
              />
            </label>
            <div className="row">
              <label className="field">
                <span>From</span>
                <input type="date" value={fromDate} onChange={(e) => setFromDate(e.target.value)} />
              </label>
              <label className="field">
                <span>To</span>
                <input type="date" value={toDate} onChange={(e) => setToDate(e.target.value)} />
              </label>
              <label className="field">
                <span>Byte units</span>
                <select value={byteUnitSystem} onChange={(e) => setByteUnitSystem(e.target.value as ByteUnitSystem)}>
                  <option value="decimal">Decimal (1 MB = 1,000,000 B)</option>
                  <option value="binary">Binary (1 MB = 1,048,576 B)</option>
                </select>
              </label>
            </div>
            {formError && <p className="status error">{formError}</p>}
            <div className="actions">
              <button type="submit" className="button primary" disabled={running}>
                {running ? "Generating…" : "Generate report"}
              </button>
              {running && (
                <button type="button" className="button ghost" onClick={() => abortRef.current?.abort()}>
                  Cancel
                </button>
              )}
            </div>
          </form>

          {run.kind !== "idle" && run.kind !== "done" && (
            <section className="panel no-print">
              <h2>Progress</h2>
              {run.sessionKey && (
                <p className="muted">
                  Session <span className="mono">{run.sessionKey}</span>
                </p>
              )}
              <ProgressLog entries={run.progress} running={running} />
              {run.kind === "failed" && <div className="callout error">{run.message}</div>}
            </section>
          )}

          {(run.kind === "running" || run.kind === "failed") && run.previous && (
            <section className="panel superseded">
              <p className="eyebrow">Previous revision</p>
              <ReportView result={run.previous} />
            </section>
          )}

          {run.kind === "done" && (
            <section className="panel">
              <ReportView result={run.result} />
              <form className="feedback no-print" onSubmit={sendFeedback}>
                <h3>Reviewer feedback</h3>
                <p className="hint">
                  Correct anything the report got wrong. The agent revises this report, and remembers rules that should
                  apply to future reports.
                </p>
                {run.result.reviewerFeedback.length > 0 && (
                  <ul className="feedback-history">
                    {run.result.reviewerFeedback.map((item, index) => (
                      <li key={index}>{item}</li>
                    ))}
                  </ul>
                )}
                <textarea
                  rows={3}
                  value={feedback}
                  onChange={(e) => setFeedback(e.target.value)}
                  placeholder="e.g. Finance only bills complete overage blocks, so round partial blocks down."
                />
                <div className="actions">
                  <button type="submit" className="button primary" disabled={feedback.trim() === ""}>
                    Send correction
                  </button>
                </div>
              </form>
              <details className="no-print run-log">
                <summary>Run log</summary>
                <ProgressLog entries={run.progress} running={false} />
              </details>
            </section>
          )}
        </div>
      </main>
    </div>
  );
};
